import { Accordion, Button, Dropdown, Icons, Loading, TextInput } from "@osttra/ds-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ApiError, getData } from "../../api/fetch.service.api";
import { applicationUrl } from "../../constants/Constants";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { getFilteredApps, SortOrder, ViewType } from "../../utils/Utils";
import ApplicationCard from "../application-card/ApplicationCard";
import ApplicationTableView from "../application-table-view/ApplicationTableView";
import ErrorPage from "../error/ErrorPage";
import Header from "../header/Header";
import "./Applications.css";

export interface OsttraApplication {
  appName: string;
  appIcon: string;
  homepageUrl: string;
}

export interface ApplicationApiResponse {
  userMail: string;
  applications: OsttraApplication[];
}

const Applications = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const appResponse = (location.state as { appResponse?: ApplicationApiResponse } | null)?.appResponse;
  const [userMail, setUserMail] = useState<string>(appResponse?.userMail || "");
  const [apps, setApps] = useState<OsttraApplication[]>(appResponse?.applications || []);
  const [searchText, setSearchText] = useState("");
  const [viewType, setViewType] = useState<ViewType>("card");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const [isError, showError] = useState(false);
  const [errorDetails, setErrorDetails] = useState<{ status?: number; message?: string }>({});
  const [loading, setLoading] = useState<boolean>(!appResponse);
  const [filteredApps, setFilteredApps] = useState<OsttraApplication[]>([]);
  const debouncedSearch = useDebouncedValue(searchText, 250);

  useEffect(() => {
    if (appResponse) {
      return;
    }

    const fetchUserAppsOnReload = async () => {
      try {
        setLoading(true);
        const data = await getData<ApplicationApiResponse>({
          url: applicationUrl,
        });

        setUserMail(data.userMail);
        setApps(data.applications);
        showError(false);
        setLoading(false);
      } catch (err: any) {
        if (err instanceof ApiError && err.status === 401) {
          navigate("/saml/login", { replace: true });
          return;
        }
        setLoading(false);
        showError(true);
        setErrorDetails({
          status: 500,
          message: "Something went wrong on our side. Please refresh the page or try again in a few minutes.",
        });
      }
    };

    fetchUserAppsOnReload();
  }, [appResponse, navigate]);

  useEffect(() => {
    const appsList = getFilteredApps(apps, debouncedSearch, sortOrder);
    setFilteredApps(appsList);
  }, [apps, debouncedSearch, sortOrder]);

  const toggleSort = (item: any) => {
    setSortOrder(item.key);
  };

  return (
    <div>
      {!isError && (
        <>
          <Header userMail={userMail} />
          <main className="container">
            {loading ? (
              <Loading size={"big"} />
            ) : (
              <>
                <div className="app-gallery-header">
                  <TextInput
                    className="search-input"
                    value={searchText}
                    icon="search"
                    inputAlign="left"
                    placeholder="Search"
                    type="text"
                    onChange={(event) => setSearchText(event.target.value)}
                  />

                  <Button
                    className="header-btn"
                    type={viewType === "card" ? "primary" : undefined}
                    variant={viewType === "card" ? undefined : "outlined"}
                    icon="cards-small"
                    onClick={() => setViewType("card")}
                  >
                    Card
                  </Button>

                  <Button
                    className="header-btn"
                    type={viewType === "table" ? "primary" : undefined}
                    variant={viewType === "table" ? undefined : "outlined"}
                    icon="list"
                    onClick={() => setViewType("table")}
                  >
                    Table
                  </Button>

                  <Dropdown
                    items={[
                      {
                        displayName: "Name",
                        key: "asc",
                        renderedDisplayName: (
                          <span>
                            Name ascending
                            <Icons.SortAlphaUp className="sort-icon" />
                          </span>
                        ),
                      },
                      {
                        displayName: "Name",
                        key: "desc",
                        renderedDisplayName: (
                          <span>
                            Name descending
                            <Icons.SortAlphaDown className="sort-icon" />
                          </span>
                        ),
                      },
                    ]}
                    onChange={(item) => toggleSort(item)}
                    label="Sort by"
                  />
                </div>

                {!apps || apps.length === 0 ? (
                  <div className="msg t-p-l t-text-center">
                    <h2>It seems you ({userMail}) are not subscribed to any OSTTRA application.</h2>
                  </div>
                ) : filteredApps && filteredApps.length > 0 ? (
                  <Accordion isExpandedDefault header="All Apps">
                    {viewType === "table" ? (
                      <ApplicationTableView applications={filteredApps} userMail={userMail} />
                    ) : (
                      <div className="t-grid t-auto">
                        {filteredApps.map((app, index) => (
                          <div className="t-col">
                            <ApplicationCard
                              key={index}
                              userMail={userMail}
                              appName={app.appName}
                              homepageUrl={app.homepageUrl}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </Accordion>
                ) : (
                  <div className="msg t-p-l t-text-center">
                    <h2>No applications found.</h2>
                  </div>
                )}
              </>
            )}
          </main>
        </>
      )}

      {isError && <ErrorPage errorCode={errorDetails.status} errorMessage={errorDetails.message} />}
    </div>
  );
};

export default Applications;
